import { z } from 'zod';
import { MAX_HOME_ITEMS, OwnedThing, Rotation, RoomShape } from './home';
import { containsBlockedWord } from './profile';

// Hustle, as Lagos Life's 🧰 Hustle (its rules read from the public client code on 23 October 2026), in London
// reality: players hire players. A job is posted on the Board (nothing is paid until someone is hired) or
// offered straight to a player who listed themselves for hire. When someone is hired the fee and the transfer
// charge are taken and held; the worker marks the job done; the poster pays (or sends it back once), and it
// pays itself a day after it was marked done. Calling it off before it is done gives the money back. Reported
// posts go to the team. A decorator works on a copy of the poster's home (see "The decorator" below). Decided
// with Jerry on 23 October 2026. All of it is content the admins edit.

export const HUSTLE_KINDS = [
  'decorate',
  'cook',
  'dj',
  'driver',
  'security',
  'photographer',
  'tutor',
  'custom',
] as const;
export type HustleKind = (typeof HUSTLE_KINDS)[number];

export const HustleKindContent = z.object({
  kind: z.enum(HUSTLE_KINDS),
  // what the poster wants ("Cook for my party") and what the worker is ("Cook")
  label: z.string().trim().min(2).max(40),
  short: z.string().trim().min(2).max(24),
  emoji: z.string().trim().min(1).max(16),
  // the most a fee can be for this kind
  maxPence: z.number().int().min(100).max(1_000_000_000),
  visible: z.boolean(),
});
export type HustleKindContent = z.infer<typeof HustleKindContent>;

export const HustleDecorContent = z.object({
  // the decorator's access to the copy: days from being hired, days more on asking, the most in all
  accessDays: z.number().int().min(1).max(60),
  moreDays: z.number().int().min(1).max(30),
  mostDays: z.number().int().min(1).max(90),
  // notes asking for changes or looks turned down, per job
  asksMax: z.number().int().min(0).max(20),
  // while the poster waits for changes they cannot call it off, unless the decorator is quiet this long
  quietHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30),
  // the most a budget for new pieces can be, and how many new pieces one look can have
  budgetMaxPence: z.number().int().min(0).max(1_000_000_000),
  piecesMax: z.number().int().min(0).max(MAX_HOME_ITEMS),
});
export type HustleDecorContent = z.infer<typeof HustleDecorContent>;
export const DEFAULT_DECOR: HustleDecorContent = {
  accessDays: 10,
  moreDays: 3,
  mostDays: 21,
  asksMax: 5,
  quietHours: 48,
  budgetMaxPence: 10_000_000,
  piecesMax: 40,
};
export const HustleContent = z.object({
  visible: z.boolean(),
  feeMinPence: z.number().int().min(100).max(1_000_000_000),
  feeMaxPence: z.number().int().min(100).max(1_000_000_000),
  // a post stays on the Board this long (a bump, once every `bumpHours`, starts its time again)
  openHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 30),
  bumpHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 7),
  // an offer waits this long for an answer; a job marked done pays itself this long after
  offerHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 7),
  payHours: z
    .number()
    .int()
    .min(1)
    .max(24 * 7),
  // jobs a player can have going at once, as the poster and as the worker; people who can apply to one post
  maxActive: z.number().int().min(1).max(20),
  maxApplicants: z.number().int().min(1).max(200),
  // a listing for hire lasts this many days, with up to this many kinds
  listDays: z.number().int().min(1).max(90),
  kindsMax: z.number().int().min(1).max(HUSTLE_KINDS.length),
  // reports from this many players take a post off the Board until the team looks
  reportsToHide: z.number().int().min(1).max(50),
  kinds: z.array(HustleKindContent).length(HUSTLE_KINDS.length),
  // the decorator's rules (missing: the defaults below)
  decor: HustleDecorContent.optional(),
});
export type HustleContent = z.infer<typeof HustleContent>;

const kind = (id: HustleKind, label: string, short: string, emoji: string): HustleKindContent => ({
  kind: id,
  label,
  short,
  emoji,
  maxPence: 10_000_000,
  visible: true,
});
export const DEFAULT_HUSTLE: HustleContent = {
  visible: true,
  feeMinPence: 1_000,
  feeMaxPence: 10_000_000,
  openHours: 72,
  bumpHours: 6,
  offerHours: 24,
  payHours: 24,
  maxActive: 3,
  maxApplicants: 30,
  listDays: 7,
  kindsMax: 4,
  reportsToHide: 3,
  kinds: [
    kind('decorate', 'Decorate my home', 'Decorator', '🛋️'),
    kind('cook', 'Cook for my party', 'Cook', '🍲'),
    kind('dj', 'DJ my party', 'DJ', '🎧'),
    kind('driver', 'Be my driver', 'Driver', '🚘'),
    kind('security', 'Be my security', 'Security', '🕶️'),
    kind('photographer', 'Photograph my event', 'Photographer', '📸'),
    kind('tutor', 'Tutor me', 'Tutor', '📚'),
    kind('custom', 'Something else', 'Hustler', '🧰'),
  ],
};
export const hustleOf = (content: { hustle?: HustleContent }) => content.hustle ?? DEFAULT_HUSTLE;
export function hustleProblems(hustle: HustleContent): string[] {
  const problems: string[] = [];
  const decor = decorOf(hustle);
  if (decor.accessDays > decor.mostDays) problems.push("Hustle: a decorator's first access is longer than the most.");
  if (new Set(hustle.kinds.map((item) => item.kind)).size !== hustle.kinds.length)
    problems.push('Hustle: each kind of job appears once.');
  if (hustle.feeMinPence > hustle.feeMaxPence) problems.push('Hustle: the smallest fee is above the largest.');
  return problems;
}
// the most a fee can be for a kind (the kind's own most, never above the largest fee)
export const hustleMax = (hustle: HustleContent, kindId: HustleKind) =>
  Math.min(hustle.feeMaxPence, hustle.kinds.find((item) => item.kind === kindId)?.maxPence ?? hustle.feeMaxPence);

// ---- the decorator (decided with Jerry on 23 October 2026, as Lagos Life's): once hired, the decorator works on
// a copy of the home the poster lives in, from anywhere, even when the poster is offline; the draft saves itself;
// nothing in the real house changes until the poster accepts a look. The poster can set a budget for new pieces,
// paid only when they accept the look. Sending a look counts as marking the job done (the fee rules above); the
// poster accepts it, sends a note asking for changes, or turns it down so the decorator tries another.

export const decorOf = (hustle: HustleContent) => hustle.decor ?? DEFAULT_DECOR;
export const DECOR_NOTE_MAX = 120;
// a new piece in a look is known by "new-" and 8 letters or digits until it is bought
export const NEW_PIECE = /^new-[a-z0-9]{8}$/;
export const isNewPiece = (id: string) => NEW_PIECE.test(id);

// draft: the decorator is working; sent: a look waits for the poster; changes: the poster sent a note;
// turned_down: the poster passed on the last look; accepted: the look is in the house
export const DECOR_STATES = ['draft', 'sent', 'changes', 'turned_down', 'accepted'] as const;
export type DecorState = (typeof DECOR_STATES)[number];

// a look: where each thing stands (the poster's own things by id, new pieces by their "new-" id) and the new
// pieces it buys; what is not placed goes to storage when it is accepted
const coordinate = z.number().finite().min(-10).max(10);
export const DecorLayout = z
  .object({
    placed: z
      .array(
        z
          .object({
            id: z.union([z.string().uuid(), z.string().regex(NEW_PIECE)]),
            x: coordinate,
            z: coordinate,
            rotation: Rotation,
          })
          .strict(),
      )
      .max(MAX_HOME_ITEMS),
    fresh: z
      .array(z.object({ id: z.string().regex(NEW_PIECE), item: z.string().min(1).max(40) }).strict())
      .max(MAX_HOME_ITEMS),
  })
  .strict();
export type DecorLayout = z.infer<typeof DecorLayout>;

export const HustleDecorView = z.object({
  jobId: z.string().uuid(),
  // who is looking: the decorator or the poster
  role: z.enum(['worker', 'poster']),
  poster: z.string(),
  worker: z.string(),
  status: z.string(),
  // the poster's home: its room and the things they own today
  room: RoomShape,
  homeLabel: z.string(),
  things: z.array(OwnedThing),
  // the decorator's draft (the decorator only) and the look sent (both)
  draft: DecorLayout.nullable(),
  look: DecorLayout.nullable(),
  // the look's number: answers about a look name it, so an old look is never accepted by mistake
  version: z.number().int(),
  state: z.enum(DECOR_STATES),
  note: z.string().nullable(),
  asksLeft: z.number().int(),
  budgetPence: z.number().int(),
  // what the new pieces in the look sent cost (the poster pays it on accepting)
  lookCostPence: z.number().int(),
  // the decorator's access: until when, the keys given (or taken back), and whether more time can be asked
  accessUntil: z.string(),
  keys: z.boolean(),
  canEdit: z.boolean(),
  moreUntil: z.string().nullable(),
  walletPence: z.number().int(),
  rules: HustleDecorContent,
  message: z.string().optional(),
});
export type HustleDecorView = z.infer<typeof HustleDecorView>;
// what a look's new pieces cost at the shop's prices
export const lookCostOf = (look: DecorLayout | null, price: (item: string) => number | undefined) =>
  (look?.fresh ?? []).reduce((sum, piece) => sum + (price(piece.item) ?? 0), 0);

// ---------- over the wire ----------

// words a player writes (a post's details, a listing's bio): no blocked words
const said = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine((text) => !containsBlockedWord(text), 'Please say it another way');
export const HUSTLE_DETAILS_MAX = 200;
export const HUSTLE_BIO_MAX = 80;

// open: on the Board; offered: sent to one player; hired: under way; done: marked done by the worker; then
// paid, refunded (called off or given up), cancelled (taken down before anyone was hired), expired, removed
export const HUSTLE_STATUSES = [
  'open',
  'offered',
  'hired',
  'done',
  'paid',
  'refunded',
  'cancelled',
  'expired',
  'removed',
] as const;
export type HustleStatus = (typeof HUSTLE_STATUSES)[number];
export const HUSTLE_LIVE: HustleStatus[] = ['open', 'offered', 'hired', 'done'];

export const HustleStats = z.object({
  // the average stars from reviews (null: none yet), how many reviews, jobs paid
  stars: z.number().nullable(),
  reviews: z.number().int(),
  jobs: z.number().int(),
});
export type HustleStats = z.infer<typeof HustleStats>;
export const HustlePerson = z.object({ lifeId: z.string().uuid(), name: z.string(), stats: HustleStats });
export type HustlePerson = z.infer<typeof HustlePerson>;

export const HustleJobView = z.object({
  id: z.string().uuid(),
  kind: z.enum(HUSTLE_KINDS),
  details: z.string(),
  feePence: z.number().int(),
  // the transfer charge on top, paid by the poster when someone is hired
  chargePence: z.number().int(),
  status: z.enum(HUSTLE_STATUSES),
  // offered straight to one player, not posted on the Board
  direct: z.boolean(),
  poster: HustlePerson,
  worker: HustlePerson.nullable(),
  // you posted it; you are (or were asked to be) the worker; you applied
  mine: z.boolean(),
  working: z.boolean(),
  applied: z.boolean(),
  // the poster sees who applied
  applicants: z.array(HustlePerson),
  applicantCount: z.number().int(),
  // when it leaves the Board, or the offer runs out; when a job marked done pays itself
  expiresAt: z.string().nullable(),
  payAt: z.string().nullable(),
  // a bump is possible from then (null: now)
  bumpFrom: z.string().nullable(),
  sentBack: z.boolean(),
  stars: z.number().int().nullable(),
  createdAt: z.string(),
  // a decorate job once someone is hired: where the decorator's work stands (null for other kinds)
  decor: z
    .object({
      state: z.enum(DECOR_STATES),
      version: z.number().int(),
      accessUntil: z.string(),
      keys: z.boolean(),
      budgetPence: z.number().int(),
      asksLeft: z.number().int(),
      note: z.string().nullable(),
      // the poster waits for changes and cannot call it off until then (null: they can)
      waitUntil: z.string().nullable(),
    })
    .nullable(),
});
export type HustleJobView = z.infer<typeof HustleJobView>;
export const HustleListingView = z.object({
  kinds: z.array(z.enum(HUSTLE_KINDS)),
  ratePence: z.number().int(),
  bio: z.string(),
  listedUntil: z.string(),
});
export type HustleListingView = z.infer<typeof HustleListingView>;
export const HustleProView = HustlePerson.extend({
  kinds: z.array(z.enum(HUSTLE_KINDS)),
  ratePence: z.number().int(),
  bio: z.string(),
});
export type HustleProView = z.infer<typeof HustleProView>;

export const HustleReply = z.object({
  // jobs you posted or offered, and jobs you were offered or hired for (live ones and the last few done)
  mine: z.array(HustleJobView),
  working: z.array(HustleJobView),
  listing: HustleListingView.nullable(),
  // the transfer charge, so the app can show the total before you hire
  charge: z.object({ bps: z.number().int(), minPence: z.number().int() }),
  walletPence: z.number().int(),
  message: z.string().optional(),
});
export type HustleReply = z.infer<typeof HustleReply>;
export const HustleBoardReply = z.object({ jobs: z.array(HustleJobView), next: z.string().nullable() });
export type HustleBoardReply = z.infer<typeof HustleBoardReply>;
export const HustleProsReply = z.object({ pros: z.array(HustleProView) });
export type HustleProsReply = z.infer<typeof HustleProsReply>;

const fee = z.number().int().min(1).max(1_000_000_000);
export const HustlePostRequest = z
  .object({ kind: z.enum(HUSTLE_KINDS), details: said(HUSTLE_DETAILS_MAX), feePence: fee })
  .strict();
export type HustlePostRequest = z.infer<typeof HustlePostRequest>;
export const HustleOfferRequest = HustlePostRequest.extend({ lifeId: z.string().uuid() }).strict();
export type HustleOfferRequest = z.infer<typeof HustleOfferRequest>;
export const HUSTLE_REPORT_REASONS = {
  scam: 'A scam or asking for money outside the game',
  offensive: 'Rude, hateful or bullying',
  sexual: 'Sexual or creepy',
  other: 'Something else',
} as const;
export type HustleReportReason = keyof typeof HUSTLE_REPORT_REASONS;
export const HustleAction = z.discriminatedUnion('action', [
  // anyone on the Board
  z.object({ action: z.literal('apply') }).strict(),
  z.object({ action: z.literal('withdraw') }).strict(),
  z
    .object({
      action: z.literal('report'),
      reason: z.enum(Object.keys(HUSTLE_REPORT_REASONS) as [HustleReportReason, ...HustleReportReason[]]),
    })
    .strict(),
  // the poster
  z.object({ action: z.literal('hire'), lifeId: z.string().uuid() }).strict(),
  z.object({ action: z.literal('bump') }).strict(),
  z.object({ action: z.literal('cancel') }).strict(),
  z.object({ action: z.literal('pay') }).strict(),
  z.object({ action: z.literal('send_back') }).strict(),
  z.object({ action: z.literal('call_off') }).strict(),
  z.object({ action: z.literal('review'), stars: z.number().int().min(1).max(5) }).strict(),
  // the poster of a decorate job: answer a look (by its number), set a budget, take the keys back or give them
  z.object({ action: z.literal('accept_look'), version: z.number().int().min(1) }).strict(),
  z
    .object({
      action: z.literal('changes'),
      version: z.number().int().min(1),
      note: said(DECOR_NOTE_MAX).pipe(z.string().min(2)),
    })
    .strict(),
  z.object({ action: z.literal('turn_down'), version: z.number().int().min(1) }).strict(),
  z.object({ action: z.literal('budget'), budgetPence: z.number().int().min(0).max(1_000_000_000) }).strict(),
  z.object({ action: z.literal('keys'), on: z.boolean() }).strict(),
  // the worker
  z.object({ action: z.literal('accept') }).strict(),
  z.object({ action: z.literal('decline') }).strict(),
  z.object({ action: z.literal('done') }).strict(),
  z.object({ action: z.literal('give_up') }).strict(),
  // the decorator: send the draft as the look (it counts as done), or ask for more time
  z.object({ action: z.literal('send_look') }).strict(),
  z.object({ action: z.literal('more_time') }).strict(),
]);
export type HustleAction = z.infer<typeof HustleAction>;
export const HustleListingRequest = z
  .object({
    kinds: z.array(z.enum(HUSTLE_KINDS)).min(1).max(HUSTLE_KINDS.length),
    ratePence: fee,
    bio: said(HUSTLE_BIO_MAX),
  })
  .strict();
export type HustleListingRequest = z.infer<typeof HustleListingRequest>;

// the team: reported posts
export const HustleReportView = z.object({
  job: z.object({
    id: z.string().uuid(),
    kind: z.enum(HUSTLE_KINDS),
    // the kind as the published content names it
    label: z.string(),
    details: z.string(),
    feePence: z.number().int(),
    status: z.enum(HUSTLE_STATUSES),
    poster: z.object({ lifeId: z.string().uuid(), name: z.string() }),
    createdAt: z.string(),
  }),
  reports: z.array(z.object({ reason: z.string(), by: z.string(), at: z.string() })),
  // the team's decision (null: open)
  decided: z.enum(['removed', 'kept']).nullable(),
});
export type HustleReportView = z.infer<typeof HustleReportView>;
export const HustleReportsReply = z.object({ reports: z.array(HustleReportView), open: z.number().int() });
export type HustleReportsReply = z.infer<typeof HustleReportsReply>;
export const HustleDecision = z
  .object({ outcome: z.enum(['remove', 'keep']), note: z.string().trim().max(300).optional() })
  .strict();
export type HustleDecision = z.infer<typeof HustleDecision>;
